import type { TeamSide, TacticalRole } from "./types.js";

/** Fase del equipo respecto al balon. */
export type TeamPhase = "attack" | "defend" | "loose" | "transition";

/**
 * Instrucciones de equipo (0 = minimo, 1 = maximo salvo mentality).
 * Inspirado en los sliders de FM / tacticas de FIFA.
 */
export interface TeamInstructions {
  /** -1 muy defensivo … +1 muy ofensivo. */
  mentality: number;
  /** Intensidad del pressing coordinado. */
  pressing: number;
  /** Amplitud del bloque en ataque. */
  width: number;
  /** Altura de la linea defensiva. */
  lineHeight: number;
  /** Compacidad horizontal del bloque (0 = estirado, 1 = linea corta). */
  lineLength: number;
  /** Compacidad vertical entre lineas. */
  compactness: number;
  /** Ritmo de juego: pase rapido vs conduccion. */
  tempo: number;
  /**
   * Cuanto se pide a medios y carrilleros que lleguen desde atras (0..1).
   *
   * 0 = nadie abandona su sitio; 1 = se suman en cuanto el balon pisa campo
   * rival. Es una instruccion y no un automatismo porque tiene coste: el que
   * llega deja un hueco detras.
   */
  lateRuns: number;
  /**
   * Pases directos (0..1): cuanto se busca el pase LARGO hacia delante frente
   * a la combinacion corta. 0,5 es neutro y no cambia nada — es el valor de
   * fabrica, asi que el online y el Partido rapido juegan como siempre. Lo
   * usan los estilos de club: el patadon lo sube, el toque lo baja.
   */
  directness: number;
  /**
   * Dejar a uno arriba (0..1): al defender, el delantero no baja; espera la
   * salida por delante del balon. 0 = nadie (fabrica). Es la firma de la
   * contra: robar atras y tener a quien darsela.
   */
  outlet: number;
}

export interface RoleProfile {
  /** Linea tactica base. */
  line: "def" | "mid" | "att";
  /** Multiplicador de amplitud (laterales y extremos). */
  widthFactor: number;
  /** Cuanto avanza la posicion base en ataque (0-1). */
  attackPush: number;
  /**
   * Ganas de chutar del rol, sobre el umbral de tiro de la IA.
   *
   * Sin esto, el umbral era igual para todos y solo lo superaba quien juega
   * pegado al area: medido con `pnpm ai:audit`, el delantero tiraba 38 veces
   * por cada 100 toques y **el resto de roles, cero**. Un medio que llega al
   * borde tiene que poder probar.
   *
   * El tiro sigue acotado por la DISTANCIA (`shootRange`), asi que subir esto
   * no hace que un central chute desde su area: solo que, ya en rango, no
   * devuelva siempre la pelota.
   */
  shootBias: number;
  /**
   * Se suma al ataque llegando DESDE DETRAS del balon.
   *
   * Es la unica forma de meter mas gente en el area sin romper nada: el bloque
   * no puede avanzar mas porque `offsideAwareX` lo recorta contra la linea de
   * fuera de juego — y esta bien que lo haga. Pero quien arranca por detras del
   * balon **nunca esta en fuera de juego**, asi que puede llegar hasta el area
   * sin pelearse con esa regla.
   *
   * El delantero no la tiene: el ya juega en el hombro del ultimo defensa.
   */
  lateRun: boolean;
  /** Cuanto se ancla al retroceder (0-1). */
  defendHold: number;
  /** Peso en el reparto de pressing. */
  pressWeight: number;
  /** Carrera en profundidad al espacio. */
  runInBehind: boolean;
  /** Sube por la banda en fase ofensiva. */
  overlap: boolean;
  /** Debe respetar la linea defensiva del equipo. */
  holdLine: boolean;
  /** Presiona al portador desde delante. */
  leadPress: boolean;
  /**
   * Multiplicador fisico (velocidad punta Y aceleracion), 1 = referencia.
   * Profundidad de rol, no solo de tactica: un central es mas lento y fuerte
   * en el duelo, un extremo/lateral vive de la velocidad. Rango deliberadamente
   * estrecho (0.92-1.08): esto es matiz de FM/FIFA, no personajes de arcade
   * con velocidades muy distintas entre si.
   */
  speedMultiplier: number;
  /**
   * Apetito de riesgo, 1 = referencia. Un jugador audaz intenta el pase
   * filtrado por un carril mas cerrado y dispara desde peor posicion; uno
   * conservador circula en corto y solo remata lo claro.
   *
   * Existe para que la IA pueda FALLAR por atrevimiento, no solo por error
   * aleatorio: un central que la juega segura y un delantero que la arriesga
   * son dos decisiones distintas ante el mismo balon, no la misma decision
   * con distinta suerte.
   */
  boldness: number;
  /** Vision de juego: pases creativos y lectura de huecos. 1 = referencia. */
  vision: number;
  /** Precision y calidad de envio. 1 = referencia. */
  passing: number;
  /** Lectura defensiva / interceptar. 1 = referencia. */
  anticipation: number;
  /** Control bajo presion y colocacion corporal. 1 = referencia. */
  placement: number;
}

export const ROLE_PROFILES: Record<TacticalRole, RoleProfile> = {
  gk: {
    line: "def",
    widthFactor: 0,
    attackPush: 0,
    shootBias: 0,
    lateRun: false,
    defendHold: 1,
    pressWeight: 0,
    runInBehind: false,
    overlap: false,
    holdLine: true,
    leadPress: false,
    speedMultiplier: 1,
    /** El portero nunca arriesga: un error suyo es gol. */
    boldness: 0.7,
    vision: 0.88,
    passing: 0.86,
    anticipation: 1.12,
    placement: 1.08,
  },
  cb: {
    line: "def",
    widthFactor: 0.35,
    attackPush: 0.18,
    shootBias: 0.5,
    lateRun: false,
    defendHold: 0.95,
    pressWeight: 0.15,
    runInBehind: false,
    overlap: false,
    holdLine: true,
    leadPress: false,
    speedMultiplier: 0.92,
    /** El central la juega segura; perder el balon atras es letal. */
    boldness: 0.75,
    vision: 0.9,
    passing: 0.94,
    anticipation: 1.1,
    placement: 1.06,
  },
  fb: {
    line: "def",
    widthFactor: 1,
    attackPush: 0.55,
    shootBias: 0.7,
    lateRun: true,
    defendHold: 0.7,
    pressWeight: 0.35,
    runInBehind: false,
    overlap: true,
    holdLine: false,
    leadPress: false,
    speedMultiplier: 1.05,
    /** El lateral asume el riesgo normal del que sube y vuelve. */
    boldness: 1.0,
    vision: 0.96,
    passing: 1.02,
    anticipation: 1.0,
    placement: 0.98,
  },
  cm: {
    line: "mid",
    widthFactor: 0.45,
    attackPush: 0.42,
    shootBias: 0.88,
    lateRun: true,
    defendHold: 0.55,
    pressWeight: 0.55,
    runInBehind: false,
    overlap: false,
    holdLine: false,
    leadPress: false,
    speedMultiplier: 1,
    /** El medio es quien intenta el pase entre lineas. */
    boldness: 1.05,
    vision: 1.1,
    passing: 1.12,
    anticipation: 1.02,
    placement: 1.04,
  },
  wm: {
    line: "mid",
    widthFactor: 1,
    attackPush: 0.5,
    shootBias: 0.92,
    lateRun: true,
    defendHold: 0.5,
    pressWeight: 0.4,
    runInBehind: false,
    overlap: false,
    holdLine: false,
    leadPress: false,
    speedMultiplier: 1.08,
    /** El extremo vive del uno contra uno y del centro dificil. */
    boldness: 1.15,
    vision: 1.06,
    passing: 1.05,
    anticipation: 0.96,
    placement: 0.94,
  },
  st: {
    line: "att",
    widthFactor: 0.55,
    attackPush: 0.72,
    shootBias: 1,
    lateRun: false,
    defendHold: 0.35,
    pressWeight: 0.85,
    runInBehind: true,
    overlap: false,
    holdLine: false,
    leadPress: true,
    speedMultiplier: 1.04,
    /** El delantero remata lo que otros no intentarian. */
    boldness: 1.25,
    vision: 0.98,
    passing: 0.94,
    anticipation: 1.06,
    placement: 0.92,
  },
};

/** Mapeo etiqueta de formacion -> rol tactico. */
export const LABEL_TO_ROLE: Record<string, TacticalRole> = {
  GK: "gk",
  CB: "cb",
  LB: "fb",
  RB: "fb",
  CM: "cm",
  LM: "wm",
  RM: "wm",
  ST: "st",
};

/** Instrucciones por equipo. Se pueden cambiar en caliente desde consola. */
/**
 * Los valores de fabrica, congelados.
 *
 * `TEAM_INSTRUCTIONS` es un objeto VIVO que el plan de equipo muta en sitio, y
 * lo lee la IA dentro de la simulacion — o sea que forma parte del estado del
 * lockstep. Dos clientes con planes distintos jugarian partidos distintos y el
 * online se partiria sin que nadie entendiera por que.
 *
 * Esta copia existe para poder volver a un punto de partida IDENTICO en todas
 * las maquinas antes de un partido online. Ver `resetTeamInstructions`.
 */
const FACTORY_INSTRUCTIONS: Record<TeamSide, TeamInstructions> = {
  home: {
    mentality: 0.2,
    pressing: 0.66,
    width: 0.72,
    lineHeight: 0.62,
    lineLength: 0.74,
    compactness: 0.78,
    /** Ritmo alto = mas tiki-taka (pase corto, un toque). */
    tempo: 0.78,
    lateRuns: 0.6,
    directness: 0.5,
    outlet: 0,
  },
  away: {
    mentality: -0.08,
    pressing: 0.52,
    width: 0.58,
    lineHeight: 0.45,
    lineLength: 0.68,
    compactness: 0.8,
    tempo: 0.55,
    /** El visitante llega menos: su plan base es mas conservador. */
    lateRuns: 0.4,
    directness: 0.5,
    outlet: 0,
  },
};

/**
 * Las instrucciones VIVAS, que es lo que lee la tactica cada tick.
 *
 * Arranca como copia de fabrica; el plan de equipo la muta en sitio.
 */
export const TEAM_INSTRUCTIONS: Record<TeamSide, TeamInstructions> = {
  home: { ...FACTORY_INSTRUCTIONS.home },
  away: { ...FACTORY_INSTRUCTIONS.away },
};

/**
 * Devuelve las instrucciones a fabrica. Se llama antes de un partido ONLINE.
 *
 * Sin esto, el plan que cada uno tuviera guardado de su ultima partida local
 * entraba en la simulacion compartida: mismo tick, mismas entradas, decisiones
 * distintas. Es el tipo de desincronizacion que no se ve venir porque el
 * culpable es un ajuste de menu, no el codigo de red.
 */
export function resetTeamInstructions(): void {
  for (const side of ["home", "away"] as const) {
    Object.assign(TEAM_INSTRUCTIONS[side], FACTORY_INSTRUCTIONS[side]);
  }
}

export const TACTICS = {
  /**
   * Llegada desde atras: duracion, descanso y ventana de arranque.
   *
   * El descanso importa tanto como la duracion. Sin el, el mismo jugador
   * reengancha la carrera en cuanto termina y deja de ser una llegada para
   * convertirse en un segundo delantero — que es justo lo que el bloque, con
   * razon, no hace.
   */
  LATE_RUN_TIME: 2.6,
  LATE_RUN_COOLDOWN: 5.5,
  /** Hay que estar por detras del balon, pero no en la otra punta. */
  LATE_RUN_MIN_BEHIND: 50,
  LATE_RUN_MAX_BEHIND: 430,
  /** El portador tiene que haber pasado de esta fraccion del campo. */
  LATE_RUN_CARRIER_ADVANCE: 0.52,
  /** Cuantos pueden llegar a la vez. Mas de esto es quedarse sin equipo. */
  LATE_RUN_MAX_CONCURRENT: 2,
  /** Segundos de contra-pressing tras perder el balon. */
  COUNTER_PRESS_TIME: 2.4,
  /**
   * Segundos que la fase de ataque sobrevive con el balon suelto (un pase en
   * el aire, un rechace todavia sin dueno). Espejo de `COUNTER_PRESS_TIME`.
   *
   * Sin esto el bloque solo empujaba mientras alguien SUJETABA el balon, que
   * es el 19% del tiempo. Se corta en seco si el rival captura.
   */
  ATTACK_HOLD_TIME: 1.2,
  /**
   * Empuje del bloque hacia la porteria rival en fase de ataque, en unidades,
   * escalado por `attackPush` de cada rol.
   *
   * Existe porque el resto del ataque se ancla al BALON
   * (`spawn.x + (ball.x - centro) * BLOCK_SLIDE_X`), y eso es circular: con el
   * balon en el centro el deslizamiento es cero, nadie se adelanta, no hay a
   * quien pasar y el balon no avanza. Medido antes de existir: en fase de
   * ataque el balon estaba de media en x=792 (el centro) y el jugador mas
   * adelantado en 902, a 154 del tercio rival.
   *
   * Un equipo real no espera a que el balon suba para subir: al recuperar, los
   * de arriba ya arrancan. Esto es esa referencia propia de la fase.
   *
   * No afecta a los centrales: `holdLine` los recorta contra la linea
   * defensiva, que es lo que se quiere — sube el bloque, no la defensa entera.
   */
  ATTACK_BLOCK_PUSH: 240,
  /** Maximo de jugadores en pressing coordinado. */
  MAX_PRESSERS: 3,
  /**
   * Linea de presion (la "line of engagement" de FM): hasta donde sale el
   * equipo a apretar al portador, en fraccion del campo desde SU porteria.
   * Sale de `pressing` —el deslizador que en el plan va de "Esperar" a
   * "Asfixiar"—: con `ENGAGE_FULL_PRESSING` o mas se presiona en todo el campo
   * (la linea es 1); por debajo la linea baja hasta `ENGAGE_MIN` en
   * `ENGAGE_LOW_PRESSING`.
   *
   * Hasta el 2026-09-28 no existia: el que presionaba salia a por el portador
   * estuviera donde estuviera y los que marcaban seguian a su hombre por todo
   * el campo, asi que los estilos de club no se distinguian — medido con
   * `pnpm style:probe`, en 3v3 el cerrojo defendia a la misma altura (51 %)
   * que la presion.
   *
   * Los dos planes de fabrica (0,66 y 0,52) quedan por encima del umbral a
   * proposito: el online y el Partido rapido presionan exactamente igual que
   * antes. Solo esperan los estilos de la carrera y el plan de quien lo baje.
   */
  ENGAGE_MIN: 0.3,
  ENGAGE_LOW_PRESSING: 0.1,
  ENGAGE_FULL_PRESSING: 0.5,
  /**
   * Cuanto se adelanta, sobre la linea de presion, el que marca a un rival que
   * esta mas alla de ella (fraccion del campo). Espera en su zona en vez de
   * irse detras de su hombre hasta el area contraria.
   */
  ENGAGE_MARK_MARGIN: 0.05,
  /**
   * Lo mismo para los de la linea de ataque: esperan mas arriba, en el medio
   * campo, como en cualquier bloque bajo de verdad. Con todos pegados a la
   * linea el cerrojo no tenia a nadie arriba y en 7v7 paso de 1,1 a 0,3 goles.
   */
  ENGAGE_FORWARD_MARGIN: 0.15,
  /**
   * Con `pressing` desde aqui ("Asfixiar"), en los modos de hasta 3 de campo
   * salen DOS a por el balon en vez de uno. Deja libre a uno de los suyos: es
   * el riesgo que define a un equipo que presiona, no un descuido.
   */
  PRESS_DOUBLE_SMALL: 0.85,
  /**
   * Dejar a uno arriba (`outlet`): a que fraccion del campo desde SU porteria
   * espera el delantero cuando el equipo defiende, entre `MIN` (outlet ~0) y
   * `MAX` (outlet 1). Acotado luego por el fuera de juego.
   */
  OUTLET_DEPTH_MIN: 0.5,
  OUTLET_DEPTH_MAX: 0.62,
  /**
   * Contraataque: segundos, tras robarle el balon al rival, en los que un
   * equipo que deja a uno arriba (`outlet`) busca el pase hacia delante, y
   * cuanto lo premia (`1 + GAIN * outlet` para un pase que avanza
   * `COUNTER_FULL_FORWARD` o mas). Con `outlet` 0 —la fabrica— no hace nada.
   */
  COUNTER_WINDOW: 3,
  COUNTER_FORWARD_GAIN: 0.8,
  COUNTER_FULL_FORWARD: 300,
  /** Holgura sobre la linea defensiva antes de activar fuera de juego tactico. */
  LINE_TOLERANCE: 28,
  /** Empuje extra de altura de linea en trampa de fuera de juego. */
  OFFSIDE_TRAP_BOOST: 0.22,
  /** Velocidad minima del balon suelto hacia nuestra porteria para activar trampa. */
  OFFSIDE_TRAP_BALL_SPEED: 260,
  /** Distancia de sombra al marcar (entre rival y porteria). */
  MARK_SHADOW: 0.42,
  /** Mas lejos que esto, un defensa no se hace cargo de un atacante. */
  MARK_MAX_DIST: 420,
  /**
   * Descuento (en unidades de distancia) por conservar la marca que ya tenias.
   * Es la histeresis que mata el "jitter": sin ella, dos rivales que se cruzan
   * invierten los costes y los defensas se intercambian las marcas a 60 Hz.
   * Medido antes de existir: 152 cambios de marca por minuto en 11v11.
   */
  MARK_STICKY_BONUS: 150,
  /**
   * Cuanto pesa el peligro de un atacante frente a la distancia pura. El metodo
   * hungaro a secas solo minimiza recorrido, asi que dejaria suelto al delantero
   * plantado en el area si esta un metro mas lejos que un lateral inofensivo.
   */
  MARK_DANGER_WEIGHT: 300,
  /**
   * Segundos entre recalculos de la asignacion. No hace falta resolverlo 60
   * veces por segundo: la asignacion optima no cambia tan rapido, y recalcular
   * menos es en si mismo lo que da marcas estables.
   */
  MARK_REFRESH: 0.35,
  /** Deslizamiento del bloque con el balon. */
  BLOCK_SLIDE_X: 0.52,
  /**
   * Cuanto MAS sigue al balon el bloque en fase de ataque, escalado por el
   * `attackPush` del rol.
   *
   * El deslizamiento base tiene coeficiente efectivo ~0,40 para el local: por
   * cada 100 unidades que avanza el balon, el bloque avanza 40. Eso hace que la
   * brecha CREZCA cuanto mas atacas, y por eso se nota justo en el ultimo
   * tercio: no es que los companeros bajen, es que no suben al mismo ritmo.
   * Medido antes de tocarlo: con el balon en x=951 el bloque estaba en x=722.
   *
   * Se escala por rol y no en bloque a proposito — es la idea de DEBER de
   * Football Manager dicha con el vocabulario que ya existe: el central (0,18)
   * apenas se suma, el delantero (0,72) sigue al balon casi entero.
   */
  ATTACK_SLIDE_BONUS: 0.7,
  BLOCK_SLIDE_Y: 0.64,
  /** Carrera en profundidad: adelanto sobre la posicion base. */
  RUN_BEHIND_PUSH: 220,
  /**
   * Cuanto se adelanta al portador el hombre de profundidad MIENTRAS se
   * construye (portador aun fuera del ultimo tercio).
   *
   * Es la referencia ofensiva que permite progresar. Va acotado por
   * `offsideAwareX`, asi que pedir mas de la cuenta no regala fueras de juego:
   * simplemente se queda en la linea.
   */
  RUN_AHEAD_BUILDUP: 210,
  /**
   * Empujon hacia la porteria rival aplicado a los planes de ataque, escalado
   * por el `attackPush` del rol. Es lo que da profundidad a un ataque cuya
   * geometria es, por lo demas, enteramente relativa al portador.
   */
  ATTACK_DEPTH_BIAS: 190,
  /** Sobrelevacion de lateral en ataque. */
  OVERLAP_PUSH: 180,

  /*
   * OCUPAR EL AREA.
   *
   * El agujero mas viejo del ataque, y estaba medido: con el balon en el ultimo
   * tercio, el area rival estaba VACIA el 69-90% del tiempo (`pnpm box:probe`),
   * y por eso el 68-84% de los tiros salian de fuera. No se disparaba de lejos
   * por gusto: no habia a quien pasarsela dentro.
   *
   * Lo que NO era: el fuera de juego. Medido en la misma sonda, con el balon
   * arriba la linea cae en x=1373-1459 —dentro del area, que empieza en 1340— y
   * el mas adelantado se quedaba en 1226-1274. Sobraban entre 104 y 208 unidades
   * de sitio LEGAL sin usar. Nadie le mandaba entrar, sencillamente.
   */
  /**
   * Deber minimo para atacar el area (`attackPush` del rol).
   *
   * Deja dentro a `st` (0,72), `fb` (0,55), `wm` (0,5) y `cm` (0,42); fuera solo
   * al central (0,18), que es el que sostiene.
   *
   * El primer intento fue 0,45 —"que entren solo los rematadores"— y no movio
   * un decimal. El motivo, medido: en 5v5 la formacion es portero + CB + dos CM
   * + ST, o sea que con ese umbral **el unico elegible era el delantero**, y ese
   * esta ocupado la mitad del tiempo (presiona el balon suelto el 12% de los
   * ticks con el balon arriba, y lo lleva otro 3%). Un plan para el que casi
   * nunca esta libre no es un plan. Con el medio dentro hay a quien mandar
   * SIEMPRE.
   *
   * Quien entra de verdad lo acota `boxRunners()`, no este numero: esto dice
   * quien PUEDE, aquel cuantos VAN.
   */
  BOX_ATTACK_PUSH: 0.4,
  /** 1 = el que lleva el balon no ocupa plaza en el area (`planBoxAttack`). 0 = como antes. */
  BOX_SKIP_OWNER: 1,
  /**
   * Desde donde se empieza a atacar el area, como fraccion del campo recorrida
   * hacia la porteria rival.
   *
   * 0,56 y no 0,66 (el ultimo tercio) a proposito: al area se ENTRA antes de
   * que llegue el balon. Esperar a que el pase salga es llegar tarde a todo.
   */
  BOX_TRIGGER_ADVANCE: 0.56,
  /** Profundidad de las estaciones de palo y del punto de penalti (u desde la linea). */
  BOX_POST_DEPTH: 78,
  BOX_SPOT_DEPTH: 158,
  /** Separacion entre estaciones a lo ancho (u desde el centro de la porteria). */
  BOX_STATION_SPREAD: 108,

  /*
   * ENTRE LINEAS.
   *
   * El hueco entre la defensa rival y su medio campo es donde se recibe de
   * cara, y donde nace todo pase en profundidad. Medido antes de esto: el hueco
   * existia (217-399 u de ancho) pero solo lo pisaba alguien LIBRE el 24-44% de
   * los ticks con balon, y por casualidad — ningun plan apuntaba ahi. Todo el
   * ataque se colocaba RELATIVO AL PORTADOR (`planAttackRun`, `planTriangleSupport`),
   * asi que el bloque orbitaba el balon y la posicion respecto al rival no la
   * miraba nadie.
   */
  /** Por debajo de esto el bloque rival esta junto: no hay donde recibir. */
  BETWEEN_MIN_GAP: 110,
  /** Como de cerca de la defensa rival se recibe, dentro del hueco. */
  BETWEEN_DEPTH: 0.62,
  /** El balon tiene que haber salido de atras para que tenga sentido ofrecerse. */
  BETWEEN_TRIGGER_ADVANCE: 0.3,
  /** Pasado esto manda `planBoxAttack`: ya no se recibe entre lineas, se remata. */
  BETWEEN_MAX_ADVANCE: 0.62,
  /**
   * Hasta donde puede alejarse del balon el que se ofrece entre lineas.
   *
   * Es el limite mas duro de todo el ataque y no lo miraba nadie: el balon raso
   * alcanza 440 unidades utiles en 11v11 sobre un campo de 1600. Un medio
   * plantado en el hueco a 600 del portador NO es una opcion de pase, es un
   * adorno — y encima se le ha quitado al equipo del triangulo de apoyo.
   * Medido: sin esto, mandar a UN solo interior al hueco hundia el balon en el
   * ultimo tercio del 44% al 26% de los ticks.
   */
  BETWEEN_MAX_FROM_BALL: 280,
  /** Y por delante del balon: recibir entre lineas es progresar, o no es eso. */
  BETWEEN_MIN_AHEAD: 40,
  /**
   * Cuanto puede desplazar el hueco a un apoyo, como mucho.
   *
   * Acotado a proposito: el interior sigue siendo una opcion de pase cercana,
   * solo que colocada en el bolsillo. Sustituir el apoyo entero por el hueco se
   * probo, y hundio el balon en el ultimo tercio del 11v11 del 44% al 22%.
   */
  BETWEEN_MAX_SHIFT: 95,
  /**
   * Llegada al SEGUNDO PALO del carrilero del lado contrario.
   *
   * El desdoble (`overlap`) solo se dispara con el balon en TU banda, asi que
   * el carrilero del otro lado no tenia ninguna instruccion en ataque y se
   * quedaba en la linea defensiva. Medido con `pnpm ai:audit`: el rol `fb`
   * recibia el 0% de los pases (2 en toda la muestra) y tocaba 44 veces por
   * cada 337 del delantero. Era un espectador.
   *
   * Con el balon en una banda, el carril del segundo palo es el espacio mas
   * grande y mas libre del campo. Es la jugada que en el futbol de verdad hace
   * el lateral que llega de atras, y la que en Football Manager separa a un
   * carrilero con deber de ATAQUE de uno con deber de defensa.
   */
  /**
   * Cuanto tiene que haber avanzado el portador, de 0 (su porteria) a 1.
   *
   * NO se usa `inFinalThird` (que exige pasar de x=1152) porque en fase de
   * ataque el balon vive en x=951: la puerta no se abria nunca y la llegada
   * quedaba muerta. Es el mismo umbral con el que arrancan las llegadas desde
   * atras, y por el mismo motivo.
   */
  FAR_POST_ADVANCE: 0.55,
  /** Cuanto tiene que estar el balon fuera del centro (fraccion del alto). */
  FAR_POST_BALL_WIDE: 0.1,
  /**
   * Cuanto tiene que salir del centro el SITIO del jugador para considerarlo
   * ancho (fraccion del alto).
   *
   * La condicion NO puede ser `overlap`, que es lo primero que probe: ese rasgo
   * solo lo tiene `fb`, y en 5v5 la formacion es portero + CB + dos CM + ST, o
   * sea que no hay ningun `fb`. Medido con una sonda: la rama se ejecutaba 0
   * veces en 5v5 y 49 en 11v11. Los "carrileros" de los lados pequenos son los
   * CM abiertos a los carriles 0,25 y 0,75, asi que lo que define la jugada es
   * el CARRIL, no la etiqueta del rol.
   */
  FAR_POST_MIN_LANE: 0.12,
  /** Altura del destino, en multiplos del semiancho de la porteria. */
  FAR_POST_Y: 1.15,
  /** Profundidad desde la linea de gol. Medio area. */
  FAR_POST_DEPTH: 130,
  /** Desplazamiento del bloque hacia el lado del balon al defender. */
  BALL_SIDE_SHIFT: 0.22,
  /** Separacion minima entre companeros (anti-amontonamiento). */
  TEAMMATE_SEPARATION: 70,
  /**
   * En 3v3/2v2 la separacion base es corta: el companero se pega al humano.
   * Multiplicador sobre TEAMMATE_SEPARATION cuando hay pocos por equipo.
   */
  SMALL_SIDE_SEP_SCALE: 1.85,
  /**
   * Distancia minima al portador que debe respetar un apoyo (u).
   * Por debajo de esto no hay linea de pase real, solo seguimiento.
   */
  MIN_SUPPORT_DIST: 145,
  /**
   * Triangulacion: apoyos en diagonal al portador (juego de posicion).
   * Dos puntas adelante + un pivote atras forman el triangulo de pase.
   */
  TRIANGLE_AHEAD: 105,
  TRIANGLE_WIDTH: 118,
  TRIANGLE_PIVOT: 78,
  /** Escala de geometria de apoyo en lados pequenos (3v3). */
  SMALL_SIDE_SUPPORT_SCALE: 1.75,
  /** Cuantos companeros ocupan vertices del triangulo. */
  TRIANGLE_SLOTS: 3,
  /** Medios espacios (juego posicional): fracciones de altura del campo. */
  HALF_SPACE_LANES: [0.32, 0.68] as const,
  /**
   * Carril de BANDA: donde se coloca un extremo cuando su equipo juega por
   * fuera. A 0,1 del alto son 100 unidades de la linea, dentro de la franja
   * que `KICK.CROSS_WING_BAND` (175) exige para poder centrar.
   *
   * Antes no existia y ese era el motivo de que la IA no centrara NUNCA: todo
   * el apoyo se planificaba en medios espacios (0,32 / 0,68 = y 320 y 680) y
   * la zona de centro empieza en y<=175. Nadie llegaba, `evaluateCross`
   * devolvia null siempre y la rama de centro no aparecia ni una vez en la
   * tabla de decisiones de `pnpm ai:audit`.
   */
  WIDE_LANES: [0.1, 0.9] as const,
  /**
   * Jugadores de campo a partir de los cuales el equipo juega por FUERA.
   *
   * Es lo que separa las identidades que pidio el diseño:
   *
   *  - Hasta 4 de campo (2v2 a 5v5) el juego es de sala: campo estrecho,
   *    superioridades cortas y nadie pegado a la linea. En futbol sala no hay
   *    extremos que desborden para centrar; hay paredes y ocupacion del eje.
   *  - De 5 en adelante (6v6, futbol 7, y arriba) hay bandas de verdad: el
   *    extremo pisa la linea, llega al fondo y centra.
   *
   * Poner esto mas bajo convierte el 5v5 en futbol 11 en pequeño, que es
   * exactamente lo que se venia a evitar.
   */
  FLANK_MIN_OUTFIELD: 5,

  /*
   * MARCADOR Y RELOJ.
   *
   * Hasta el 2026-09-28 la IA no los veia: jugaba igual 0-0 en el primer
   * minuto que perdiendo en el ultimo. Ahora cada equipo tiene una URGENCIA
   * (`ai/situation.ts`), de -1 (proteger lo que tiene) a +1 (ir a por el gol),
   * que ajusta su plan SIN sustituirlo: un cerrojo que pierde sale, pero menos
   * que un equipo de presion que pierde. Empatados, 0 y el plan intacto — asi
   * que hasta el primer gol el partido es el mismo, bit a bit, que antes.
   */
  /**
   * Desde que fraccion del partido se reacciona a ir perdiendo de uno (o
   * ganando), a partir de cual se reacciona con dos o mas abajo, y cuando la
   * reaccion es completa. Ir 0-2 a mitad de partido ya pide algo; ir 0-1, no.
   */
  SITUATION_START: 0.5,
  SITUATION_START_BIG_DEFICIT: 0.25,
  SITUATION_FULL: 0.85,
  /**
   * Cuanto pesa la diferencia: `[de 1, de 2, de 3 o mas]`. Perdiendo de uno o
   * de dos se va a por todas; de tres, el partido esta perdido y se empuja a
   * medias. Ganando de uno se cierra todo; de tres, se sigue jugando.
   */
  SITUATION_CHASE_BY_DEFICIT: [1, 1, 0.5] as const,
  SITUATION_PROTECT_BY_LEAD: [1, 0.6, 0.25] as const,
  /**
   * Remontar (urgencia +1): cuanto se SUMA a cada instruccion del plan. Sube
   * la presion (con la de fabrica llega a "Asfixiar": dos al balon en los
   * modos pequeños), la linea, la mentalidad y las llegadas, y juega mas
   * directo.
   */
  CHASE_MENTALITY: 0.6,
  CHASE_PRESSING: 0.3,
  CHASE_LINE_HEIGHT: 0.3,
  CHASE_LATE_RUNS: 0.4,
  CHASE_DIRECTNESS: 0.2,
  CHASE_TEMPO: 0.15,
  /**
   * En los modos de sala (menos de `SITUATION_PUSH_MIN_OUTFIELD` de campo) el
   * que persigue NO adelanta el bloque: solo aprieta arriba y chuta antes.
   * `CHASE_SHAPE_SMALL` es la fraccion de mentalidad, linea, llegadas, balon
   * directo y ritmo que se aplica ahi.
   *
   * Medido con `pnpm score:probe` (IA contra IA; "remontada" = ir perdiendo
   * al 60 % del partido y no perderlo). En 3v3, palanca a palanca con 200
   * partidos: subir la linea las bajaba del 26 % al 15 %, y la mentalidad al
   * 18 % — con dos de campo, el que se va arriba deja al portero solo y le
   * cazan a la contra. En 5v5 ninguna ayudaba. Sin ellas, 3v3 26 → 22 % y
   * 5v5 28 → 26 % (400 partidos: dentro del ruido). En 7v7 y 11v11 el paquete
   * entero las SUBE (27 → 33 %, 21 → 32 %, 240 partidos): hay gente para
   * cubrir.
   */
  CHASE_SHAPE_SMALL: 0,
  SITUATION_PUSH_MIN_OUTFIELD: 5,
  /** Y cuanto baja el umbral de tiro: se prueba desde donde antes se pasaba. */
  CHASE_SHOOT_GAIN: 0.35,
  /**
   * Proteger (urgencia -1): cuanto se RESTA. La presion baja lo bastante para
   * que la linea de presion caiga a medio campo (con la de fabrica) y el
   * bloque espere; nadie llega desde atras y el ritmo baja.
   */
  PROTECT_MENTALITY: 0.6,
  PROTECT_PRESSING: 0.45,
  PROTECT_LINE_HEIGHT: 0.3,
  PROTECT_LATE_RUNS: 0.5,
  PROTECT_TEMPO: 0.3,
  PROTECT_COMPACTNESS: 0.15,
  /**
   * Perder tiempo: segundos de MAS que el portero asienta el balon antes de
   * sacar, sin nadie encima, cuando su equipo protege. Con presion saca igual
   * que siempre: aguantar con un rival delante es regalarlo.
   */
  PROTECT_GK_SETTLE: 1.4,
} as const;
